import { WandSparklesIcon } from "lucide-react";
import { useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasNumberInput,
  CanvasSimpleSelect,
} from "@/components/canvas-editor/components/Inputs";
import { NodeDocs } from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  MagicAudienceShape,
  magicAudienceType,
  inclusionOperators,
  InclusionOperator,
  MagicAudienceProps,
  inclusionOperatorOptions,
  metadataType,
  Dimensions,
} from "@/components/canvas-editor/shapes/shapes";
import { MIN_H, ToolStateNode } from "@/components/canvas-editor/utils";
import { SimpleTooltip } from "@/components/ui/tooltip";

export function isMagicAudienceShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is MagicAudienceShape {
  return shape.type === magicAudienceType;
}

export class MagicAudienceTool extends ToolStateNode {
  static override id = magicAudienceType;

  static override uiToolDef = (editor: Editor) => ({
    id: magicAudienceType,
    icon: WandSparklesIcon,
    label: MagicAudienceShapeUtil.docs.title,
    defaultColor: MagicAudienceShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(magicAudienceType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={MagicAudience}
        util={MagicAudienceShapeUtil}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: magicAudienceType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class MagicAudienceShapeUtil extends BindableUtil<MagicAudienceShape> {
  static override migrations = createMigrations(magicAudienceType);
  static override type = magicAudienceType;

  static override docs = {
    defaultColor: "green" as const,
    title: "Magic Audience",
    description:
      "Include or exclude the users in a Magic Audience from the feed.",
  };

  override getType() {
    return magicAudienceType;
  }

  static override props: RecordProps<MagicAudienceShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    magicAudienceId: T.number,
    operator: T.literalEnum(...inclusionOperators),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 300,
      minW: 300,
      h: MIN_H,
      magicAudienceId: 42,
      operator: inclusionOperators[0],
      metadata: undefined,
    };
  }

  override component(shape: MagicAudienceShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <MagicAudience
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function MagicAudience({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: MagicAudienceShape;
  className?: string;
  updateShape: (
    shape: MagicAudienceShape,
    props: Partial<MagicAudienceProps>,
  ) => void;
}) {
  const variant = shape.props.operator === "in" ? "green" : "red";
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={shape.props.metadata?.color || variant}
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={MagicAudienceShapeUtil.docs}
        />
        <BlockBody>
          <SimpleTooltip message="The Magic Audience ID">
            <CanvasNumberInput
              aria-label="Magic Audience ID"
              value={shape.props.magicAudienceId}
              className="flex-1"
              placeholder="Magic Audience ID"
              min={0}
              onValueChange={(id) =>
                updateShape(shape, { magicAudienceId: id })
              }
            />
          </SimpleTooltip>
          <CanvasSimpleSelect
            aria-label="Inclusion Operator"
            className="w-24"
            value={shape.props.operator}
            onValueChange={(v) =>
              updateShape(shape, { operator: v as InclusionOperator })
            }
            options={inclusionOperatorOptions}
          />
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
